/**
 * Tonnie Languages Academy — the words both citizen apps say, as key strings,
 * and what a refusal of the Academy's endpoints means for the person. Pure:
 * the server decides everything; this only names it.
 */
import { DOMAIN_ERROR_CODES, apiErrorCode } from '@tonnie/foundation';
import type {
  ExternalWorldMissionStatus,
  LanguageAcademyLanguage,
  LanguageAcademySharedField,
  LanguageAcademyUnavailableReason,
} from '@tonnie/types';

/** Each language, by its own name (the picker shows it the way its speakers write it). */
export const LANGUAGE_ACADEMY_LANGUAGE_KEYS = {
  en: 'qultureup.academy.language.names.en',
  'pt-PT': 'qultureup.academy.language.names.ptPT',
  es: 'qultureup.academy.language.names.es',
  it: 'qultureup.academy.language.names.it',
} as const satisfies Record<LanguageAcademyLanguage, string>;

/**
 * What the path says its missions are in, per course language — a whole
 * sentence each, the language named in the reader's own language ("Missions in
 * English…", "Missões em inglês…"); `{{partner}}` is the school.
 */
export const LANGUAGE_ACADEMY_PATH_NOTE_KEYS = {
  en: 'qultureup.academy.progress.path.titlesNote.en',
  'pt-PT': 'qultureup.academy.progress.path.titlesNote.ptPT',
  es: 'qultureup.academy.progress.path.titlesNote.es',
  it: 'qultureup.academy.progress.path.titlesNote.it',
} as const satisfies Record<LanguageAcademyLanguage, string>;

/** What each shared field is, in the person's words. */
export const LANGUAGE_ACADEMY_FIELD_KEYS = {
  NAME: 'qultureup.academy.consent.fields.name',
  EMAIL: 'qultureup.academy.consent.fields.email',
  LEARNING_LANGUAGE: 'qultureup.academy.consent.fields.learningLanguage',
  LEVEL: 'qultureup.academy.consent.fields.level',
  UI_LANGUAGE: 'qultureup.academy.consent.fields.uiLanguage',
  BIRTH_DATE: 'qultureup.academy.consent.fields.birthDate',
  PHONE: 'qultureup.academy.consent.fields.phone',
  NATIONALITY: 'qultureup.academy.consent.fields.nationality',
} as const satisfies Record<LanguageAcademySharedField, string>;

/** Why the Academy cannot be opened here, said honestly. */
export const LANGUAGE_ACADEMY_UNAVAILABLE_KEYS = {
  ASSESSMENT_REQUIRED: 'qultureup.academy.unavailable.assessmentRequired',
  NOT_CONFIGURED: 'qultureup.academy.unavailable.notConfigured',
  PARTNER_PENDING: 'qultureup.academy.unavailable.partnerPending',
} as const satisfies Record<LanguageAcademyUnavailableReason, string>;

/** A mission's state on the Academy's path, in the person's words (`in_progress` takes `{{percent}}`). */
export const LANGUAGE_ACADEMY_MISSION_STATUS_KEYS = {
  completed: 'qultureup.academy.progress.mission.completed',
  in_progress: 'qultureup.academy.progress.mission.inProgress',
  not_started: 'qultureup.academy.progress.mission.notStarted',
} as const satisfies Record<ExternalWorldMissionStatus, string>;

/** What the person can fix for the school to take them: their name, or a verified email. */
export const LANGUAGE_ACADEMY_FIXES = ['name', 'email'] as const;
export type LanguageAcademyFix = (typeof LANGUAGE_ACADEMY_FIXES)[number];

/** Every sentence a refusal can be — the guard in dynamic-key-coverage.spec.ts holds each to en.json. */
export const LANGUAGE_ACADEMY_REFUSAL_MESSAGE_KEYS = [
  'qultureup.academy.unavailable.assessmentRequired',
  'qultureup.academy.unavailable.notConfigured',
  'qultureup.academy.unavailable.partnerPending',
  'qultureup.academy.refusal.consentRequired',
  'qultureup.academy.refusal.languageFixed',
  'qultureup.academy.consent.outdated',
  'qultureup.academy.refusal.profileIncomplete.name',
  'qultureup.academy.refusal.profileIncomplete.email',
  'qultureup.academy.refusal.profileIncomplete.both',
  'qultureup.academy.refusal.generic',
] as const;
export type LanguageAcademyRefusalMessageKey =
  (typeof LANGUAGE_ACADEMY_REFUSAL_MESSAGE_KEYS)[number];

export interface LanguageAcademyRefusal {
  /** The sentence to show — a key; `{{partner}}` is the school's name. */
  messageKey: LanguageAcademyRefusalMessageKey;
  /** What the person can fix, in order — each is a link to where it is fixed. */
  fixes: LanguageAcademyFix[];
}

const GENERIC = 'qultureup.academy.refusal.generic' satisfies LanguageAcademyRefusalMessageKey;

/**
 * A refusal of the Academy's endpoints, by its machine code — never the
 * server's English sentence. An unknown failure (the network, a 5xx) is the
 * generic "could not be opened, try again".
 */
export function languageAcademyRefusalOf(error: unknown): LanguageAcademyRefusal {
  const data =
    typeof error === 'object' && error !== null
      ? ((error as { data?: unknown }).data as Record<string, unknown> | undefined)
      : undefined;
  switch (apiErrorCode(error)) {
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_UNAVAILABLE: {
      const reason = data?.['reason'];
      return {
        messageKey:
          reason === 'NOT_CONFIGURED' || reason === 'PARTNER_PENDING'
            ? LANGUAGE_ACADEMY_UNAVAILABLE_KEYS[reason]
            : GENERIC,
        fixes: [],
      };
    }
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_ASSESSMENT_REQUIRED:
      return { messageKey: LANGUAGE_ACADEMY_UNAVAILABLE_KEYS.ASSESSMENT_REQUIRED, fixes: [] };
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_CONSENT_REQUIRED:
      return { messageKey: 'qultureup.academy.refusal.consentRequired', fixes: [] };
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_LANGUAGE_FIXED:
      return { messageKey: 'qultureup.academy.refusal.languageFixed', fixes: [] };
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_CONSENT_OUTDATED:
      return { messageKey: 'qultureup.academy.consent.outdated', fixes: [] };
    case DOMAIN_ERROR_CODES.LANGUAGE_ACADEMY_PROFILE_INCOMPLETE: {
      const missing = Array.isArray(data?.['missing']) ? (data?.['missing'] as unknown[]) : [];
      const fixes = LANGUAGE_ACADEMY_FIXES.filter((fix) => missing.includes(fix));
      return {
        messageKey:
          fixes.length === 2
            ? 'qultureup.academy.refusal.profileIncomplete.both'
            : fixes[0] === 'name'
              ? 'qultureup.academy.refusal.profileIncomplete.name'
              : 'qultureup.academy.refusal.profileIncomplete.email',
        fixes: fixes.length > 0 ? [...fixes] : ['name', 'email'],
      };
    }
    default:
      return { messageKey: GENERIC, fixes: [] };
  }
}

/** The link that fixes each refusal, as words. */
export const LANGUAGE_ACADEMY_FIX_KEYS = {
  name: 'qultureup.academy.refusal.fixName',
  email: 'qultureup.academy.refusal.fixEmail',
} as const satisfies Record<LanguageAcademyFix, string>;

/**
 * Where the QultureUp page says why it was sent back from the Academy: the
 * Academy page, read before the journey's assessment, returns the person to
 * QultureUp with this query value (web `?academy=…`, phone param `academy`).
 */
export const LANGUAGE_ACADEMY_RETURN_PARAM = 'academy';
export const LANGUAGE_ACADEMY_RETURN_ASSESSMENT_REQUIRED = 'assessment-required';
